/**
 * AdminScopesPage — RBAC scope assignments UI
 * ==============================================
 *
 * Where a super-admin grants any other admin "you can manage just this event"
 * / "just this school" / "this church and its descendants in the hierarchy"
 * permissions. Backed by the `admin_scopes` collection (see backend
 * routes/admin_scopes.py).
 *
 * Two panels:
 *   1. Create-scope form (left): pick user + scope type + entity + role.
 *   2. Live list (right): every active scope assignment with revoke button.
 *
 * Designed to scale: meta endpoint returns up to 500 users, 300 events, etc.
 * — for larger orgs we'd add server-side search but for now in-form filtering
 * keeps it snappy.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Trash2, ShieldCheck, Loader2, Plus, Search, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const adminFetch = async (path, opts = {}) => {
  // The platform stores its admin JWT under `eventflow_token` (set by LoginPage);
  // we accept the other legacy keys too so this also works under tests.
  const token = localStorage.getItem('eventflow_token')
    || localStorage.getItem('admin_token')
    || localStorage.getItem('token');
  const r = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.detail || `HTTP ${r.status}`);
  }
  return r.json();
};

const SCOPE_HELP = {
  global: 'All modules, all entities (super-admin-equivalent).',
  module: 'All entities inside a specific module (e.g. "every event").',
  event: 'One specific event only.',
  school: 'One specific school tenant only.',
  church: 'One specific church only.',
  church_level: 'A node in the church hierarchy + its descendants (HQ → Province → District → Assembly → Home Group).',
};

const ROLE_DESC = {
  viewer: 'Read-only.',
  editor: 'Read + create/update.',
  admin: 'Full control including delete and user mgmt.',
};

const AdminScopesPage = () => {
  const qc = useQueryClient();

  const { data: meta, isLoading: metaLoading } = useQuery({
    queryKey: ['rbac-meta'],
    queryFn: () => adminFetch('/api/admin/rbac/scopes/meta'),
  });

  const [filterUser, setFilterUser] = useState('');
  const [filterScopeType, setFilterScopeType] = useState('');

  const { data: scopesData } = useQuery({
    queryKey: ['rbac-scopes', filterUser, filterScopeType],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filterUser) qs.set('user_id', filterUser);
      if (filterScopeType) qs.set('scope_type', filterScopeType);
      const sfx = qs.toString() ? `?${qs}` : '';
      return adminFetch(`/api/admin/rbac/scopes${sfx}`);
    },
  });

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-emerald-600" />
          Admin scopes
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Grant granular access to individual events, schools, churches, hierarchy levels, or entire modules.
          Scopes stack with the role-based defaults — you only need to add a scope when you want to <em>narrow</em> someone's reach.
        </p>
      </div>

      <GeneralAdminPanel meta={meta} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ScopeForm meta={meta} metaLoading={metaLoading} />
        <ScopeList
          scopes={scopesData?.scopes || []}
          meta={meta}
          filterUser={filterUser}
          setFilterUser={setFilterUser}
          filterScopeType={filterScopeType}
          setFilterScopeType={setFilterScopeType}
          onRevoke={(id) => {
            adminFetch(`/api/admin/rbac/scopes/${id}`, { method: 'DELETE' })
              .then(() => {
                toast.success('Scope revoked');
                qc.invalidateQueries({ queryKey: ['rbac-scopes'] });
              })
              .catch((e) => toast.error(e.message));
          }}
          onImpersonate={async (userId) => {
            // Save current super-admin token + flip to the impersonated token.
            // Hard-reload so React Query and all in-memory state pick up the
            // new identity from scratch.
            try {
              const orig = localStorage.getItem('eventflow_token')
                || localStorage.getItem('admin_token')
                || localStorage.getItem('token');
              const r = await adminFetch('/api/admin/rbac/impersonate', {
                method: 'POST',
                body: JSON.stringify({ user_id: userId }),
              });
              localStorage.setItem('admin_impersonation', JSON.stringify({
                original_token: orig,
                target_id: r.user.id,
                target_email: r.user.email,
                target_name: r.user.name,
                started_at: new Date().toISOString(),
              }));
              localStorage.setItem('eventflow_token', r.token);
              localStorage.setItem('admin_token', r.token);
              localStorage.setItem('token', r.token);
              toast.success(`Now viewing as ${r.user.name || r.user.email}`);
              setTimeout(() => { window.location.href = '/admin'; }, 600);
            } catch (e) {
              toast.error(e.message || 'Could not start impersonation');
            }
          }}
        />
      </div>
    </div>
  );
};

/* ────────────────── Form ────────────────── */

// ─── General Admin (iter 575) ───────────────────────────────────────
// Broad staff access that is not tied to one module: pick the person, tick the
// sections of the admin panel they may open, choose how far they may go inside
// them. The role carries nothing on its own, so what is ticked here is exactly
// what they get.
const GeneralAdminPanel = ({ meta }) => {
  const qc = useQueryClient();
  const [userId, setUserId] = useState('');
  const [level, setLevel] = useState('editor');
  const [sections, setSections] = useState([]);

  const { data } = useQuery({
    queryKey: ['general-admins'],
    queryFn: () => adminFetch('/api/admin/rbac/general-admins'),
  });
  const available = data?.sections_available || meta?.modules || [];
  const levels = data?.levels || ['viewer', 'editor', 'admin'];
  const current = data?.general_admins || [];

  const save = useMutation({
    mutationFn: () => adminFetch('/api/admin/rbac/general-admins', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, sections, level }),
    }),
    onSuccess: (res) => {
      toast.success(res.sections?.length
        ? `${res.email} is a General Admin over ${res.sections.length} section(s)`
        : `${res.email} is a General Admin with no sections yet`);
      qc.invalidateQueries({ queryKey: ['general-admins'] });
      qc.invalidateQueries({ queryKey: ['rbac-scopes'] });
      setSections([]);
      setUserId('');
    },
    onError: (e) => toast.error(e.message),
  });

  const toggle = (module) => setSections((prev) => (
    prev.includes(module) ? prev.filter((m) => m !== module) : [...prev, module]));

  const edit = (row) => {
    setUserId(row.user_id);
    setSections(row.sections.map((s) => s.module));
    setLevel(row.sections[0]?.level || 'editor');
  };

  return (
    <Card data-testid="general-admin-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-indigo-600" /> General Admin
        </CardTitle>
        <CardDescription>
          Broad admin access for staff who aren&apos;t tied to one module. Sits below Platform
          Admin and above the module-specific admins. It carries nothing on its own — tick the
          sections they may open and they get exactly those.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Person</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="general-admin-user">
                <SelectValue placeholder="Choose an admin account" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {(meta?.users || []).map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`general-admin-user-${u.id}`}>
                    {u.name || u.email} — {u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>How far inside each section</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger data-testid="general-admin-level"><SelectValue /></SelectTrigger>
              <SelectContent>
                {levels.map((l) => (
                  <SelectItem key={l} value={l} data-testid={`general-admin-level-${l}`}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Sections of the admin panel</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="general-admin-sections">
            {available.map((module) => {
              const on = sections.includes(module);
              return (
                <button
                  key={module}
                  type="button"
                  onClick={() => toggle(module)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    on ? 'bg-indigo-600 border-indigo-600 text-white'
                       : 'bg-white border-slate-300 text-slate-700 hover:border-indigo-400'
                  }`}
                  data-testid={`general-admin-section-${module}`}
                >
                  {module}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Nothing ticked means the admin home only. Sections you untick are switched off.
          </p>
        </div>

        <Button
          onClick={() => save.mutate()}
          disabled={!userId || save.isPending}
          data-testid="general-admin-save"
        >
          {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          : <Plus className="h-4 w-4 mr-2" />}
          Save General Admin
        </Button>

        {current.length > 0 && (
          <div className="pt-2 border-t border-slate-200" data-testid="general-admin-list">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
              Current General Admins
            </p>
            <div className="space-y-2">
              {current.map((row) => (
                <div
                  key={row.user_id}
                  className="flex items-start justify-between gap-3 p-3 rounded-lg bg-slate-50"
                  data-testid={`general-admin-row-${row.user_id}`}
                >
                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      {row.name || row.email}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {row.sections.length === 0 && (
                        <span className="text-xs text-slate-500">Admin home only</span>
                      )}
                      {row.sections.map((s) => (
                        <Badge key={s.module} variant="secondary" className="text-[10px]">
                          {s.module} · {s.level}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => edit(row)}
                    data-testid={`general-admin-edit-${row.user_id}`}
                  >
                    Change
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


const ScopeForm = ({ meta, metaLoading }) => {
  const qc = useQueryClient();
  const [userId, setUserId] = useState('');
  const [scopeType, setScopeType] = useState('event');
  const [scopeId, setScopeId] = useState('');
  const [module, setModule] = useState('');
  const [role, setRole] = useState('viewer');
  const [userSearch, setUserSearch] = useState('');

  const filteredUsers = useMemo(() => {
    const list = meta?.users || [];
    if (!userSearch) return list;
    const q = userSearch.toLowerCase();
    return list.filter((u) => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
  }, [meta?.users, userSearch]);

  const entityOptions = useMemo(() => {
    if (!meta) return [];
    if (scopeType === 'event') return meta.events;
    if (scopeType === 'school') return meta.schools;
    if (scopeType === 'church') return meta.churches;
    if (scopeType === 'church_level') return (meta.church_levels || []).map((l) => ({ id: l.id, name: `${l.level || 'level'}: ${l.name}` }));
    return [];
  }, [meta, scopeType]);

  const needsScopeId = ['event', 'school', 'church', 'church_level'].includes(scopeType);
  const needsModule = scopeType === 'module';

  const create = useMutation({
    mutationFn: () => adminFetch('/api/admin/rbac/scopes', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        scope_type: scopeType,
        scope_id: needsScopeId ? scopeId : null,
        module: (needsModule || ['event', 'school', 'church', 'church_level'].includes(scopeType)) ? (module || null) : null,
        role,
      }),
    }),
    onSuccess: () => {
      toast.success('Scope granted');
      qc.invalidateQueries({ queryKey: ['rbac-scopes'] });
      setScopeId('');
    },
    onError: (e) => toast.error(e.message),
  });

  const canSubmit = userId && (!needsScopeId || scopeId) && (!needsModule || module) && role;

  return (
    <Card data-testid="rbac-scope-form">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Plus className="h-4 w-4 text-emerald-600" />
          Grant a new scope
        </CardTitle>
        <CardDescription>Re-granting an existing user/scope simply upgrades their role — no duplicates.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {metaLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            <div>
              <Label className="text-xs">User</Label>
              <div className="relative mt-1">
                <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
                <Input
                  placeholder="Search by name or email…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-7 text-xs h-8"
                  data-testid="rbac-user-search"
                />
              </div>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger className="mt-2" data-testid="rbac-user-select">
                  <SelectValue placeholder="Select admin user…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {filteredUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      <span className="font-medium">{u.name || '—'}</span>
                      <span className="text-xs text-slate-500 ml-2">{u.email}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Scope type</Label>
                <Select value={scopeType} onValueChange={(v) => { setScopeType(v); setScopeId(''); }}>
                  <SelectTrigger data-testid="rbac-scope-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(meta?.scope_types || []).map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-1">{SCOPE_HELP[scopeType]}</p>
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="rbac-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(meta?.roles || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-1">{ROLE_DESC[role]}</p>
              </div>
            </div>

            {needsScopeId && (
              <div>
                <Label className="text-xs">{scopeType === 'church_level' ? 'Hierarchy node' : `Specific ${scopeType.replace('_', ' ')}`}</Label>
                <Select value={scopeId} onValueChange={setScopeId}>
                  <SelectTrigger data-testid="rbac-scope-id-select">
                    <SelectValue placeholder={`Select ${scopeType.replace('_', ' ')}…`} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {entityOptions.length === 0 ? (
                      <SelectItem value="__none" disabled>No entities available</SelectItem>
                    ) : entityOptions.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-xs">{needsModule ? 'Module' : 'Module (optional — restricts the scope to one module)'}</Label>
              <Select value={module || '__any'} onValueChange={(v) => setModule(v === '__any' ? '' : v)}>
                <SelectTrigger data-testid="rbac-module-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {!needsModule && <SelectItem value="__any">— Any module —</SelectItem>}
                  {(meta?.modules || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={() => create.mutate()}
              disabled={!canSubmit || create.isPending}
              className="w-full"
              data-testid="rbac-create-scope-btn"
            >
              {create.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Grant scope
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

/* ────────────────── List ────────────────── */

const ScopeList = ({ scopes, meta, filterUser, setFilterUser, filterScopeType, setFilterScopeType, onRevoke, onImpersonate }) => {
  const userById = useMemo(() => {
    const m = {};
    (meta?.users || []).forEach((u) => { m[u.id] = u; });
    return m;
  }, [meta?.users]);

  const roleBadge = (r) => ({
    viewer: 'bg-slate-100 text-slate-700',
    editor: 'bg-sky-100 text-sky-700',
    admin: 'bg-rose-100 text-rose-700',
  }[r] || 'bg-slate-100 text-slate-700');

  return (
    <Card data-testid="rbac-scope-list">
      <CardHeader>
        <CardTitle className="text-base">Active scope grants ({scopes.length})</CardTitle>
        <div className="flex gap-2 pt-2">
          <Select value={filterUser || '__all'} onValueChange={(v) => setFilterUser(v === '__all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs" data-testid="rbac-filter-user"><SelectValue placeholder="Filter by user" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__all">— All users —</SelectItem>
              {(meta?.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterScopeType || '__all'} onValueChange={(v) => setFilterScopeType(v === '__all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs w-40" data-testid="rbac-filter-scope-type"><SelectValue placeholder="Filter by type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">— All types —</SelectItem>
              {(meta?.scope_types || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {scopes.length === 0 ? (
          <p className="text-xs text-slate-500 py-8 text-center">No scope grants yet — the form on the left creates the first one.</p>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {scopes.map((s) => {
              const u = userById[s.user_id];
              return (
                <div key={s.id} className="flex items-center gap-3 p-3 rounded-md border bg-slate-50 hover:bg-white transition">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-800">{u?.name || s.user_id}</span>
                      <Badge className={`text-[10px] ${roleBadge(s.role)}`}>{s.role}</Badge>
                      <Badge variant="outline" className="text-[10px]">{s.scope_type}</Badge>
                      {s.module && <Badge variant="secondary" className="text-[10px]">{s.module}</Badge>}
                    </div>
                    <p className="text-xs text-slate-600 mt-1 truncate">
                      {s.label || s.scope_id || (s.scope_type === 'global' ? 'All modules' : s.scope_type === 'module' ? `All ${s.module || ''}` : s.scope_id) || ''}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                    title="View the platform as this user (impersonate)"
                    onClick={() => onImpersonate && onImpersonate(s.user_id)}
                    data-testid={`rbac-impersonate-${s.user_id}`}
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                    onClick={() => onRevoke(s.id)}
                    data-testid={`rbac-revoke-${s.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminScopesPage;
